import { CameraOutlined, CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Image, Upload } from "antd";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import type { UploadFile, UploadChangeParam } from "antd/es/upload/interface";
import { RequiredBadge } from "./RequiredBadge";
import {
	EVENT_IMAGE_QUALITY,
	EVENT_FALLBACK_IMAGE_PATH,
} from "../../../../shared/constants/event.constants";

interface EventImage {
	file?: File;
	preview?: string;
	picUrl?: string;
	originalName?: string;
}

interface EventImageUploadProps {
	value?: EventImage[];
	onChange?: (images: EventImage[]) => void;
	systemType?: string;
	uploadUrl?: string;
	maxImages?: number;
	primaryColor?: string;
	fallbackImagePath?: string;
}

const reorder = <T,>(list: T[], startIndex: number, endIndex: number): T[] => {
	const result = Array.from(list);
	const [removed] = result.splice(startIndex, 1);
	result.splice(endIndex, 0, removed);
	return result;
};

const resizeFile = (
	file: File,
	type: "file" | "base64",
): Promise<File | string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onerror = () => reject(new Error("Failed to read file"));
		reader.readAsDataURL(file);
		reader.onload = (event) => {
			const img = document.createElement("img");
			img.onerror = () => reject(new Error("Failed to load image"));
			img.src = event.target?.result as string;
			img.onload = () => {
				const canvas = document.createElement("canvas");
				const MAX_WIDTH = 1920;
				const MAX_HEIGHT = 1080;
				let width = img.width;
				let height = img.height;

				if (width > height) {
					if (width > MAX_WIDTH) {
						height *= MAX_WIDTH / width;
						width = MAX_WIDTH;
					}
				} else {
					if (height > MAX_HEIGHT) {
						width *= MAX_HEIGHT / height;
						height = MAX_HEIGHT;
					}
				}

				canvas.width = width;
				canvas.height = height;
				const ctx = canvas.getContext("2d");
				ctx?.drawImage(img, 0, 0, width, height);

				if (type === "base64") {
					resolve(canvas.toDataURL("image/jpeg", EVENT_IMAGE_QUALITY));
				} else {
					canvas.toBlob(
						(blob) => {
							if (blob) {
								const resizedFile = new File([blob], file.name, {
									type: "image/jpeg",
									lastModified: Date.now(),
								});
								resolve(resizedFile);
							} else {
								resolve(file);
							}
						},
						"image/jpeg",
						EVENT_IMAGE_QUALITY,
					);
				}
			};
		};
	});
};

export const EventImageUpload = ({
	value = [],
	onChange,
	systemType = "イベント",
	uploadUrl,
	maxImages = 10,
	primaryColor = "#99CA29",
	fallbackImagePath = EVENT_FALLBACK_IMAGE_PATH,
}: EventImageUploadProps) => {
	const handleDragEnd = (result: {
		destination?: { index: number };
		source: { index: number };
	}) => {
		if (!result.destination) return;

		const reorderedImages = reorder(
			value,
			result.source.index,
			result.destination.index,
		);

		onChange?.(reorderedImages);
	};

	const handleUploadChange = async (
		info: UploadChangeParam<UploadFile>,
		index: number,
	) => {
		const file = info.file.originFileObj || (info.file as unknown as File);
		if (!file || !(file instanceof File)) {
			console.warn("No valid file object found in upload");
			return;
		}

		try {
			const _file = (await resizeFile(file, "file")) as File;
			const _preview = (await resizeFile(file, "base64")) as string;

			const newImages = [...value];
			while (newImages.length <= index) {
				newImages.push({});
			}
			newImages[index] = {
				file: _file,
				preview: _preview,
				originalName: file.name,
			};
			onChange?.(newImages);
		} catch (error) {
			console.error("Error processing image:", error);
		}
	};

	const handleRemove = (index: number) => {
		const newImages = value.filter((_, i) => i !== index);
		if (newImages.length === 0) {
			newImages.push({});
		}
		onChange?.(newImages);
	};

	const handleAdd = () => {
		if (value.length < maxImages) {
			onChange?.([...value, {}]);
		}
	};

	const displayImages = value.length > 0 ? value : [{}];

	return (
		<div className="mb-6">
			<div className="mb-2">
				<span className="font-bold">{`${systemType}画像`}</span>
				<RequiredBadge />
			</div>
			<p className="text-xs text-gray-400 mb-4">
				※項目をドラッグして表示順を自由に調整できます
			</p>

			<DragDropContext onDragEnd={handleDragEnd}>
				<Droppable droppableId="droppableOccasionImage" direction="vertical">
					{(provided) => (
						<div ref={provided.innerRef} {...provided.droppableProps}>
							{displayImages.map((imageData, index) => {
								const draggableId = `image-${index}`;
								const hasImage = imageData.preview || imageData.picUrl;

								return (
									<Draggable
										key={draggableId}
										draggableId={draggableId}
										index={index}
									>
										{(dragProvided) => (
											<div
												ref={dragProvided.innerRef}
												{...dragProvided.draggableProps}
												{...dragProvided.dragHandleProps}
												className="border border-dashed border-gray-300 rounded-lg bg-white p-4 mb-4"
												style={dragProvided.draggableProps.style}
											>
												<div className="mb-2 text-center">
													<span className="font-medium">
														{`${systemType}画像（${index + 1}）`}
													</span>
												</div>
												<Upload
													accept=".jpg,.jpeg,.png,.gif,.webp"
													maxCount={1}
													showUploadList={false}
													beforeUpload={() => false}
													onChange={(info) => handleUploadChange(info, index)}
												>
													{hasImage ? (
														<div className="flex justify-center">
															<Image
																preview={false}
																src={(() => {
																	if (imageData.preview)
																		return imageData.preview;
																	if (!imageData.picUrl)
																		return fallbackImagePath;
																	if (
																		imageData.picUrl.startsWith("http://") ||
																		imageData.picUrl.startsWith("https://") ||
																		imageData.picUrl.startsWith("/")
																	) {
																		return imageData.picUrl;
																	}
																	return uploadUrl
																		? `${uploadUrl}${imageData.picUrl}`
																		: imageData.picUrl;
																})()}
																alt={`${systemType}画像`}
																fallback={fallbackImagePath}
																className="max-h-[200px] cursor-pointer object-contain"
															/>
														</div>
													) : (
														<div className="flex justify-center items-center bg-gray-50 border border-gray-300 rounded-lg h-[200px] cursor-pointer">
															<CameraOutlined className="text-3xl text-gray-400" />
														</div>
													)}
												</Upload>
												{displayImages.length > 1 && (
													<div className="text-right mt-2">
														<Button
															size="small"
															danger
															onClick={() => handleRemove(index)}
															icon={<CloseOutlined />}
														>
															削除する
														</Button>
													</div>
												)}
											</div>
										)}
									</Draggable>
								);
							})}
							{provided.placeholder}
							{displayImages.length < maxImages && (
								<div
									className="border border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer"
									onClick={handleAdd}
								>
									<span style={{ color: primaryColor }}>
										<PlusOutlined className="mr-2" />
										画像追加
									</span>
								</div>
							)}
						</div>
					)}
				</Droppable>
			</DragDropContext>
		</div>
	);
};
